"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import type { GrowCardData } from "./grow-card";
import { paint } from "./grow-card-carousel.fields";
import styles from "./grow-card-carousel.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/* 620ms — slow enough that the colour wash doesn't read as a flash. */
export const ACCENT_MS = 620;

type AccentLayer = { accent?: string; accentDeep?: string; id: string };

/** The retiring layer, carrying the opacity its fade-out has to start from. */
type OutgoingLayer = AccentLayer & { from: number };

type FadeState = { prev: OutgoingLayer | null; current?: AccentLayer };

export type AccentFade = FadeState & {
  /** Attach to the layer drawing `current`; the hook measures that node. */
  currentRef: (node: HTMLElement | null) => void;
};

/**
 * Two STATIC layers and the timer that retires the outgoing one, so a colour
 * change costs an opacity animation, not a per-frame re-raster (AC32).
 */
export function useAccentFade(
  id: string | undefined,
  accent?: string,
  accentDeep?: string
): AccentFade {
  const [fade, setFade] = useState<FadeState>(() => ({
    current: id === undefined ? undefined : {
      accent,
      accentDeep,
      id
    },
    prev: null
  }));
  const currentNode = useRef<HTMLElement | null>(null);
  const currentRef = useCallback((node: HTMLElement | null) => {
    currentNode.current = node;
  }, []);

  /* Both updates go through the setter callback — depending on fade.current
     directly would re-run this effect on its own state update. */
  useEffect(() => {
    if (id === undefined) {
      return undefined;
    }

    /* WAT-11671: measured HERE, one render before the class flip, because a
       flipped layer has already lost its fade-in and reports the base 1. The
       fade-out animation starts from this value — Blink creates no transition
       for a property a just-cancelled animation was producing, so a
       transition hard-cut the layer to 0 instead. */
    const from = layerOpacity(currentNode.current);

    setFade(f => (f.current?.id === id ? f : {
      current: {
        accent,
        accentDeep,
        id
      },
      prev: f.current ? {
        ...f.current,
        from
      } : null
    }));
    const done = setTimeout(() => {
      setFade(f => (f.prev ? {
        current: f.current,
        prev: null
      } : f));
    }, ACCENT_MS);
    return () => clearTimeout(done);
  }, [accent, accentDeep, id]);

  return {
    ...fade,
    currentRef
  };
}

/**
 * The opacity an outgoing layer starts its fade from: whatever a running
 * fade-in had reached, or 1 for a layer that had settled.
 */
function layerOpacity(node: HTMLElement | null): number {
  if (!node || typeof window === "undefined") {
    return 1;
  }

  const measured = Number.parseFloat(window.getComputedStyle(node).opacity);
  return Number.isFinite(measured) ? Math.min(1, Math.max(0, measured)) : 1;
}

/** The backdrop cross-fade, and the only thing the timer re-renders. */
function BloomImpl({ bands, card }: { bands: React.ReactNode; card?: GrowCardData }) {
  const fade = useAccentFade(card?.id, paint(card?.accent), paint(card?.accent_deep));

  return (
    <>
      {fade.prev ? (
        <BloomLayer
          accent={fade.prev.accent}
          accentDeep={fade.prev.accentDeep}
          bands={bands}
          fade="out"
          from={fade.prev.from}
          /* WAT-11671: the card's own id, NEVER suffixed by direction. The
             layer that was incoming under this key becomes outgoing under it,
             so a second accent change inside the window reuses the node and
             the fade-out resumes from the opacity it reached. */
          key={fade.prev.id}
        />
      ) : null}
      {fade.current ? (
        <BloomLayer
          accent={fade.current.accent}
          accentDeep={fade.current.accentDeep}
          bands={bands}
          fade={fade.prev ? "in" : undefined}
          key={fade.current.id}
          layerRef={fade.currentRef}
        />
      ) : null}
    </>
  );
}
export const Bloom = withSafeRender(BloomImpl, "Bloom");

/* One card's accent, so the gradients stay static and rasterise once. */
function BloomLayer({
  accent,
  accentDeep,
  bands,
  fade,
  from,
  layerRef
}: {
  accent?: string;
  accentDeep?: string;
  bands: React.ReactNode;
  fade?: "in" | "out";
  from?: number;
  layerRef?: (node: HTMLElement | null) => void;
}) {
  const fading = fade === "in" ? styles.bloomIn : fade === "out" ? styles.bloomOut : "";

  return (
    <div
      aria-hidden="true"
      className={fading ? `${styles.bloom} ${fading}` : styles.bloom}
      ref={layerRef}
      style={
        {
          "--gcc-accent": accent,
          "--gcc-accent-deep": accentDeep,
          /* Read by gcc-bloom-out. Undefined on any other layer, so React
             leaves the property off and the keyframe's own fallback stands. */
          "--gcc-fade-from": fade === "out" ? String(from ?? 1) : undefined
        } as React.CSSProperties
      }
    >
      {bands}
    </div>
  );
}
